import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, CircleAlert } from 'lucide-react';
import { useAccessToken } from '@/auth/useAccessToken';
import { useActiveApplications } from '@/features/applications/useApplications';
import { ticketsApi } from './ticketsApi';
import { STATUS_LABELS } from './StatusBadge';
import type { TicketCategory, TicketPriority, TicketStatus } from './schema';
import { ApiError } from '@/lib/apiClient';

const CATEGORY_OPTIONS: TicketCategory[] = ['Hardware', 'Application', 'Reports', 'Networking', 'Uncategorized'];
const PRIORITY_OPTIONS: TicketPriority[] = ['Low', 'Normal', 'High', 'Urgent'];

const STATUS_ORDER: TicketStatus[] = [
  'Pending',
  'InProgress',
  'WaitingOnSomeone',
  'PendingSoftwareRelease',
  'Completed'
];

/**
 * IT-only panel on the ticket detail: assign (agent / me / unassign) and change
 * status. Rendered by TicketDetail only for IT roles. Mutations invalidate the
 * detail, my-tickets, and queue caches so every view reflects the change.
 */
export function TicketItControls({
  ticketId,
  ticketNumber,
  assigneeId,
  status,
  autoCloseArmed,
  autoCloseDueAt,
  category,
  application,
  priority
}: {
  ticketId: string;
  ticketNumber: number;
  assigneeId: string | null;
  status: TicketStatus;
  autoCloseArmed: boolean;
  autoCloseDueAt: string | null;
  category: TicketCategory;
  application: string | null;
  priority: TicketPriority;
}) {
  const getAccessToken = useAccessToken();
  const queryClient = useQueryClient();
  // "Vanish": when closing a ticket, suppress the requester's notification email.
  const [vanish, setVanish] = useState(false);

  const agents = useQuery({
    queryKey: ['agents'],
    queryFn: ({ signal }) => ticketsApi.listAgents(getAccessToken, signal),
    staleTime: 5 * 60_000
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tickets', 'detail', ticketNumber] });
    void queryClient.invalidateQueries({ queryKey: ['tickets', 'mine'] });
    void queryClient.invalidateQueries({ queryKey: ['tickets', 'queue'] });
  };

  const assign = useMutation({
    mutationFn: (newAssigneeId: string | null) => ticketsApi.assignTicket(ticketId, newAssigneeId, getAccessToken),
    onSuccess: invalidate
  });

  const changeStatus = useMutation({
    mutationFn: (vars: { next: TicketStatus; vanish: boolean }) =>
      ticketsApi.changeStatus(ticketId, vars.next, getAccessToken, vars.vanish),
    onSuccess: invalidate
  });

  const setAutoClose = useMutation({
    mutationFn: (armed: boolean) => ticketsApi.setAutoClose(ticketId, armed, getAccessToken),
    onSuccess: invalidate
  });

  // Re-triage: category / priority / (conditional) application.
  const apps = useActiveApplications();
  const [cat, setCat] = useState<TicketCategory>(category);
  const [prio, setPrio] = useState<TicketPriority>(priority);
  const [app, setApp] = useState(application ?? '');
  const classifyDirty = cat !== category || prio !== priority || (app || null) !== (application ?? null);
  const classifyInvalid = cat === 'Application' && !app;

  const reclassify = useMutation({
    mutationFn: () =>
      ticketsApi.reclassify(
        ticketId,
        { category: cat, application: cat === 'Application' ? app : null, priority: prio },
        getAccessToken
      ),
    onSuccess: invalidate
  });

  const me = agents.data?.find((a) => a.isMe);
  const conflict =
    (assign.error as ApiError)?.status === 409 || (changeStatus.error as ApiError)?.status === 409;
  const busy = assign.isPending || changeStatus.isPending || setAutoClose.isPending;
  const dueText = autoCloseDueAt
    ? new Date(autoCloseDueAt).toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    : null;

  return (
    <div className="rounded-xl border bg-card p-4 mb-6">
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">IT controls</div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs text-muted-foreground">Assignee</span>
          <div className="mt-1 flex items-center gap-2">
            <select
              value={assigneeId ?? ''}
              disabled={busy || agents.isPending}
              onChange={(e) => assign.mutate(e.target.value || null)}
              className="flex-1 rounded-md border bg-background px-3 py-1.5 text-sm"
            >
              <option value="">Unassigned</option>
              {agents.data?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.displayName}{a.isMe ? ' (me)' : ''}
                </option>
              ))}
            </select>
            {me && assigneeId !== me.id && (
              <button
                type="button"
                disabled={busy}
                onClick={() => assign.mutate(me.id)}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs hover:bg-muted disabled:opacity-50"
              >
                <UserPlus className="w-3.5 h-3.5" /> Assign to me
              </button>
            )}
          </div>
        </label>

        <div className="block">
          <label className="block">
            <span className="text-xs text-muted-foreground">Status</span>
            <select
              value={status}
              disabled={busy}
              onChange={(e) => changeStatus.mutate({ next: e.target.value as TicketStatus, vanish })}
              className="mt-1 w-full rounded-md border bg-background px-3 py-1.5 text-sm"
            >
              {STATUS_ORDER.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </label>
          <label
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer"
            title="Vanish — close the ticket silently, without emailing the requester"
          >
            <input
              type="checkbox"
              checked={vanish}
              onChange={(e) => setVanish(e.target.checked)}
              className="rounded border-input"
            />
            <img src="/vanish.png" alt="" className="w-3.5 h-3.5 object-contain" /> Vanish on close (no email)
          </label>
        </div>
      </div>

      <div className="mt-4 border-t pt-3">
        <div className="text-xs text-muted-foreground mb-2">Classification</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-muted-foreground">Category</span>
            <select
              value={cat}
              disabled={busy || reclassify.isPending}
              onChange={(e) => setCat(e.target.value as TicketCategory)}
              className="mt-1 w-full rounded-md border bg-background px-3 py-1.5 text-sm"
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-muted-foreground">Priority</span>
            <select
              value={prio}
              disabled={busy || reclassify.isPending}
              onChange={(e) => setPrio(e.target.value as TicketPriority)}
              className="mt-1 w-full rounded-md border bg-background px-3 py-1.5 text-sm"
            >
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </label>
        </div>

        {cat === 'Application' && (
          <label className="block mt-3">
            <span className="text-xs text-muted-foreground">Which application?</span>
            <select
              value={app}
              disabled={busy || reclassify.isPending || apps.isPending}
              onChange={(e) => setApp(e.target.value)}
              className="mt-1 w-full rounded-md border bg-background px-3 py-1.5 text-sm"
            >
              <option value="">{apps.isPending ? 'Loading…' : '— Select an application —'}</option>
              {apps.data?.map((a) => (
                <option key={a.id} value={a.name}>{a.name}</option>
              ))}
            </select>
          </label>
        )}

        {classifyDirty && (
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              disabled={classifyInvalid || reclassify.isPending}
              onClick={() => reclassify.mutate()}
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 disabled:opacity-60"
            >
              {reclassify.isPending ? 'Saving…' : 'Save classification'}
            </button>
            <button
              type="button"
              disabled={reclassify.isPending}
              onClick={() => { setCat(category); setPrio(priority); setApp(application ?? ''); }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Reset
            </button>
            {classifyInvalid && <span className="text-xs text-destructive">Pick an application.</span>}
          </div>
        )}
      </div>

      <div className="mt-4 border-t pt-3">
        <label
          className="inline-flex items-center gap-2 text-sm cursor-pointer"
          title="Arm auto-close: the ticket completes after 3 business days unless the requester or a watcher replies. They get a daily 8:30 AM reminder until then."
        >
          <input
            type="checkbox"
            checked={autoCloseArmed}
            disabled={busy}
            onChange={(e) => setAutoClose.mutate(e.target.checked)}
            className="rounded border-input"
          />
          Close if no response
          <span className="text-xs text-muted-foreground">(auto-complete after 3 business days)</span>
        </label>
        {autoCloseArmed && dueText && (
          <div className="mt-1.5 text-xs text-amber-700 dark:text-amber-300">
            Auto-closes {dueText} unless the requester or a watcher replies. Daily reminders sent at 8:30 AM ET.
          </div>
        )}
      </div>

      {conflict && (
        <div className="mt-3 text-xs text-amber-700 dark:text-amber-300 inline-flex items-center gap-1.5">
          <CircleAlert className="w-3.5 h-3.5" /> This ticket changed since you loaded it — refresh and try again.
        </div>
      )}
    </div>
  );
}
